/**
 * dashboardRefresh — Refresco manual centralizado del dashboard.
 *
 * NO recarga la página. NO reinicia sesión. NO toca el editor ni la
 * conversación abierta.
 *
 * Estrategia:
 *   1. Pide en paralelo los endpoints clave (user/me, history, workspace,
 *      pro/usage, user/settings) usando el token JWT del storage.
 *   2. Emite un CustomEvent `damyxia:refresh-dashboard` con el payload
 *      consolidado. Los contextos (UserContext, WorkspaceContext,
 *      HistoryContext, MetricsContext) escuchan este evento y actualizan
 *      su estado si están montados.
 *   3. Los fetch fallidos NO bloquean al resto — se registran en consola
 *      y el evento se emite con los datos disponibles.
 *
 * Los contextos que aún no expongan un handler para este evento simplemente
 * no reciben datos; el refresco no rompe nada.
 */
import { API_URL } from '../config/api';
import { getToken } from '../utils/authToken';

const ENDPOINTS = [
  { key: 'user',       path: '/user/me' },
  { key: 'history',    path: '/history/list' },
  { key: 'workspace_external', path: '/workspace/external' },
  { key: 'workspace_internal', path: '/workspace/internal' },
  { key: 'pro_usage',  path: '/pro/usage' },
  { key: 'settings',   path: '/user/settings' },
];

async function _fetchOne(path, token) {
  const url = `${API_URL}${path}`;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

/**
 * refreshDashboard — punto único de entrada.
 * Retorna el payload consolidado por si el llamador quiere usarlo directo.
 */
export async function refreshDashboard() {
  const token = getToken();

  const results = await Promise.allSettled(
    ENDPOINTS.map(({ path }) => _fetchOne(path, token))
  );

  const payload = {};
  ENDPOINTS.forEach(({ key }, i) => {
    const r = results[i];
    payload[key] = r.status === 'fulfilled' ? r.value : null;
  });

  // Emitimos evento global para que cualquier contexto suscrito refresque.
  try {
    window.dispatchEvent(new CustomEvent('damyxia:refresh-dashboard', { detail: payload }));
  } catch { /* jsdom / SSR safety */ }

  return payload;
}

export default refreshDashboard;
